/**
 * Formatea un timestamp a fecha y hora local corta.
 * @param {string|null} value
 * @returns {string}
 */
const formatDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

/**
 * Bloque de texto largo con título; se omite si no hay contenido.
 * @param {Object} props
 * @param {string} props.title
 * @param {string|null} props.value
 * @returns {JSX.Element|null}
 */
const TextBlock = ({ title, value }) => {
  if (!value) return null
  return (
    <div className="mb-3">
      <h6 className="detail-label mb-1">{title}</h6>
      <p className="mb-0 text-body" style={{ whiteSpace: 'pre-wrap' }}>
        {value}
      </p>
    </div>
  )
}

/**
 * Vista de detalle de un incidente de seguridad (SGSI), pensada para el modal.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.incident - Incidente con `assets` embebidos.
 * @returns {JSX.Element}
 */
const IncidentDetail = ({ incident }) => {
  return (
    <div className="incident-detail p-1">
      <div className="row g-3 mb-3">
        <div className="col-sm-6">
          <h6 className="detail-label mb-1">Detectado</h6>
          <p className="mb-0 small">{formatDateTime(incident.DetectedAt)}</p>
        </div>
        <div className="col-sm-6">
          <h6 className="detail-label mb-1">Respuesta antes de</h6>
          <p className="mb-0 small">{formatDateTime(incident.ResponseDueAt)}</p>
        </div>
        <div className="col-sm-6">
          <h6 className="detail-label mb-1">Reportado por</h6>
          <p className="mb-0 small">{incident.ReportedBy || '—'}</p>
        </div>
        <div className="col-sm-6">
          <h6 className="detail-label mb-1">Origen de detección</h6>
          <p className="mb-0 small">{incident.DetectionSource || '—'}</p>
        </div>
      </div>

      <TextBlock title="Descripción" value={incident.Description} />

      <div className="mb-3">
        <h6 className="detail-label mb-2">Activos afectados</h6>
        {incident.assets?.length ? (
          <div className="d-flex flex-wrap gap-2">
            {incident.assets.map((asset) => (
              <span
                key={asset.Id}
                className="badge bg-body-secondary text-body border fw-normal"
                title={asset.Type}
              >
                {asset.Code} · {asset.Name}
              </span>
            ))}
          </div>
        ) : (
          <p className="mb-0 small text-body-secondary">
            Ningún activo asociado.
          </p>
        )}
      </div>

      {incident.PersonalDataAffected && (
        <div className="alert alert-warning py-2 px-3 small mb-3">
          <i className="fa-solid fa-user-shield me-2"></i>
          Afectó datos personales.
          {incident.ProdhabRequired
            ? ` Requiere notificación a PRODHAB · notificado: ${formatDateTime(
                incident.ProdhabNotifiedAt
              )}`
            : ' No requiere notificación a PRODHAB.'}
        </div>
      )}

      <TextBlock title="Contención" value={incident.Containment} />
      <TextBlock title="Erradicación" value={incident.Eradication} />
      <TextBlock title="Causa raíz" value={incident.RootCause} />
      <TextBlock
        title="Acciones correctivas"
        value={incident.CorrectiveActions}
      />

      <div className="border-top pt-3 mt-3 small text-body-secondary">
        <div>
          Registrado por {incident.CreatedBy || '—'} el{' '}
          {formatDateTime(incident.CreatedAt)}
        </div>
        {incident.Status === 'Cerrado' && (
          <div>
            Cerrado por {incident.ClosedBy || '—'} el{' '}
            {formatDateTime(incident.ClosedAt)}
          </div>
        )}
      </div>

      <style>{`
        .incident-detail .detail-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--gm-text-muted);
        }
      `}</style>
    </div>
  )
}

export default IncidentDetail
